import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    AlertTriangle, Ship, Anchor, FileText, DollarSign, Users, Container, Truck,
    Briefcase, Package, Clock, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
    CheckCircle2, XCircle, AlertCircle, Activity, Calendar, Bell, Search,
    Flame, Zap, Globe, ShieldAlert, Banknote, Receipt, Mail, Phone,
    ThermometerSun, Snowflake, Box, Gauge, MapPin, Plane,
} from 'lucide-react';

// ============== MOCK DATA ==============

const CRITICAL_KPIS = [
    { label: 'Delayed Shipments', value: 14, delta: '+3', trend: 'up', tone: 'red', icon: AlertTriangle, sub: 'vs yesterday' },
    { label: 'Customs Holds', value: 7, delta: '+2', trend: 'up', tone: 'amber', icon: ShieldAlert, sub: '3 require action today' },
    { label: 'Demurrage Risk', value: '$48.2K', delta: '+$6.1K', trend: 'up', tone: 'orange', icon: Flame, sub: '12 containers exposed' },
    { label: 'Open Exceptions', value: 23, delta: '−5', trend: 'down', tone: 'rose', icon: Zap, sub: '6 unassigned' },
    { label: "Today's Revenue", value: '$184K', delta: '+12%', trend: 'up', tone: 'emerald', icon: Banknote, sub: 'GP $42K · 22.8%' },
    { label: 'SLA Compliance', value: '94.2%', delta: '+0.8%', trend: 'up', tone: 'sky', icon: Gauge, sub: '7d rolling' },
];

const PRIORITY_EVENTS = [
    { id: 1, title: 'MSC EUGENIA — vessel rolled to next sailing', shipment: 'SHP-208741', impact: 'CRITICAL', owner: 'Unassigned', age: '2h', tag: 'Vessel Rollover', tone: 'red' },
    { id: 2, title: 'CBP exam hold — container MSCU8472291', shipment: 'SHP-208712', impact: 'HIGH', owner: 'Priya R.', age: '4h', tag: 'Customs Hold', tone: 'amber' },
    { id: 3, title: 'VGM cutoff missed for booking BKG-99821', shipment: 'SHP-208701', impact: 'HIGH', owner: 'Mark T.', age: '5h', tag: 'Missed Cutoff', tone: 'amber' },
    { id: 4, title: 'CMACGM CARTAGENA delayed ETA Hamburg by 36h', shipment: 'SHP-208680', impact: 'MEDIUM', owner: 'Ashish V.', age: '6h', tag: 'ETA Change', tone: 'sky' },
    { id: 5, title: 'BL released without payment — Acme Imports ($14.2K)', shipment: 'SHP-208612', impact: 'CRITICAL', owner: 'Finance', age: '7h', tag: 'Unpaid Release', tone: 'red' },
    { id: 6, title: 'AES filing rejected — invalid HTS code', shipment: 'SHP-208599', impact: 'HIGH', owner: 'Compliance', age: '9h', tag: 'Filing Failure', tone: 'amber' },
    { id: 7, title: 'Reefer temp deviation — RFCU2391029 (8.4°C)', shipment: 'SHP-208591', impact: 'CRITICAL', owner: 'Unassigned', age: '11h', tag: 'Reefer Alert', tone: 'red' },
    { id: 8, title: 'Customer escalation — Northstar Logistics (3rd contact)', shipment: '—', impact: 'HIGH', owner: 'Sarah L.', age: '13h', tag: 'Escalation', tone: 'amber' },
];

const EXPORT_MILESTONES = [
    { stage: 'Booking Confirmed', count: 142, change: '+8' },
    { stage: 'Container Pickup', count: 96, change: '+4' },
    { stage: 'Cargo Received', count: 78, change: '−2' },
    { stage: 'Customs Cleared', count: 64, change: '+11' },
    { stage: 'Gated In', count: 52, change: '+5' },
    { stage: 'VGM Submitted', count: 49, change: '+5' },
    { stage: 'SI Submitted', count: 47, change: '+3' },
    { stage: 'BL Approved', count: 41, change: '+7' },
    { stage: 'Vessel Departed', count: 36, change: '+9' },
    { stage: 'POD Confirmed', count: 28, change: '+12' },
];

const IMPORT_MILESTONES = [
    { stage: 'Pre-Alert Received', count: 124, change: '+6' },
    { stage: 'IGM/AMS/ENS Filed', count: 117, change: '+4' },
    { stage: 'Vessel Arrived', count: 89, change: '+11' },
    { stage: 'Container Discharged', count: 72, change: '+8' },
    { stage: 'Customs Cleared', count: 58, change: '+5' },
    { stage: 'DO Released', count: 51, change: '+7' },
    { stage: 'Out-Gated', count: 46, change: '+6' },
    { stage: 'Final Delivery', count: 39, change: '+12' },
    { stage: 'Empty Returned', count: 34, change: '+10' },
];

const EXCEPTIONS_FEED = [
    { id: 'EX-921', type: 'Vessel Rollover', count: 4, severity: 'Critical', tone: 'red' },
    { id: 'EX-922', type: 'Port Congestion', count: 8, severity: 'High', tone: 'amber' },
    { id: 'EX-923', type: 'Customs Hold/Exam', count: 7, severity: 'High', tone: 'amber' },
    { id: 'EX-924', type: 'Missed Cutoff', count: 3, severity: 'High', tone: 'amber' },
    { id: 'EX-925', type: 'Doc Mismatch', count: 11, severity: 'Medium', tone: 'sky' },
    { id: 'EX-926', type: 'Late VGM/SI', count: 5, severity: 'Medium', tone: 'sky' },
    { id: 'EX-927', type: 'Demurrage/Detention', count: 12, severity: 'High', tone: 'amber' },
    { id: 'EX-928', type: 'Blank Sailings', count: 2, severity: 'Medium', tone: 'sky' },
    { id: 'EX-929', type: 'Reefer Temp Alert', count: 1, severity: 'Critical', tone: 'red' },
    { id: 'EX-930', type: 'DG Compliance', count: 2, severity: 'Critical', tone: 'red' },
];

const FINANCIAL_PULSE = {
    revenueToday: 184320,
    revenueMTD: 3942100,
    grossProfit: 42180,
    gpMargin: 22.9,
    receivables: { current: 412900, d30: 188400, d60: 94100, d90: 42700 },
    unbilled: 76420,
    payables: 218600,
    overdue: 136800,
    invoicesGenerated: 38,
    paymentsReceived: 22,
    negativeMargin: 3,
    revenueLeakage: 8240,
};

const CUSTOMER_PULSE = [
    { name: 'Acme Imports', tier: 'VIP', shipments: 18, ageHrs: 2, status: 'On track', tone: 'emerald' },
    { name: 'Northstar Logistics', tier: 'VIP', shipments: 12, ageHrs: 13, status: 'Escalation', tone: 'red' },
    { name: 'Indo Pacific Trading', tier: 'Gold', shipments: 9, ageHrs: 6, status: 'Pending docs', tone: 'amber' },
    { name: 'Mediterranean Foods', tier: 'Gold', shipments: 7, ageHrs: 4, status: 'On track', tone: 'emerald' },
    { name: 'BlueRiver Apparel', tier: 'Silver', shipments: 5, ageHrs: 8, status: 'ETA changed', tone: 'sky' },
];

const COMMS_KPI = {
    openTickets: 31,
    avgResponseMin: 18,
    pendingQueries: 14,
    statusUpdatesSent: 124,
    proactiveAlerts: 56,
};

const DOC_MONITOR = {
    missingDocs: 19,
    pendingHBL: 8,
    pendingMBL: 4,
    pendingCommercialInvoice: 6,
    pendingCOO: 3,
    incorrectConsignee: 2,
    weightDisc: 4,
    hsCodeIssues: 5,
    filingDeadlines24h: 11,
};

const COMPLIANCE = [
    { code: 'AES', filings: 38, failed: 1, deadline: '+04:20', tone: 'emerald' },
    { code: 'ISF', filings: 22, failed: 2, deadline: '+02:10', tone: 'amber' },
    { code: 'AMS', filings: 19, failed: 0, deadline: '+06:00', tone: 'emerald' },
    { code: 'ICS2', filings: 14, failed: 1, deadline: '+01:30', tone: 'red' },
    { code: 'ACI/AFR', filings: 9, failed: 0, deadline: '+12:00', tone: 'emerald' },
];

const CONTAINER_EQUIP = {
    emptyAvailable: 248,
    inYard: 412,
    utilization: 81,
    avgDetentionDays: 4.2,
    demurrageExposure: 48200,
    chassisShortage: 6,
    damaged: 9,
    soc: 124,
    coc: 286,
};

const CARRIER_PULSE = [
    { carrier: 'Maersk', reliability: 88, blank: 0, space: 'Open', rateChg: '−2%', tone: 'emerald' },
    { carrier: 'MSC', reliability: 79, blank: 1, space: 'Tight', rateChg: '+5%', tone: 'amber' },
    { carrier: 'CMA CGM', reliability: 84, blank: 0, space: 'Open', rateChg: '0%', tone: 'sky' },
    { carrier: 'Hapag-Lloyd', reliability: 91, blank: 0, space: 'Open', rateChg: '−1%', tone: 'emerald' },
    { carrier: 'ONE', reliability: 76, blank: 1, space: 'Tight', rateChg: '+3%', tone: 'amber' },
    { carrier: 'COSCO', reliability: 72, blank: 2, space: 'Closed', rateChg: '+8%', tone: 'red' },
];

const SALES_PULSE = {
    newQuotes: 27,
    rfqs: 14,
    spotRequests: 9,
    winRate: 38,
    pipeline: 412900,
    onboarding: 4,
    topLanes: [
        { lane: 'NYC → RTM', quotes: 12, revenue: 184200, gp: 18.4 },
        { lane: 'SHA → LAX', quotes: 9, revenue: 142100, gp: 14.1 },
        { lane: 'NSA → JNB', quotes: 6, revenue: 98700, gp: 22.8 },
        { lane: 'SIN → HAM', quotes: 5, revenue: 76300, gp: 19.2 },
    ],
};

// ============== UI ATOMS ==============

const TONES = {
    red: { bg: 'bg-rose-500/10', text: 'text-rose-500', ring: 'ring-rose-500/30', dot: 'bg-rose-500' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', ring: 'ring-amber-500/30', dot: 'bg-amber-500' },
    orange: { bg: 'bg-orange-500/10', text: 'text-orange-500', ring: 'ring-orange-500/30', dot: 'bg-orange-500' },
    rose: { bg: 'bg-pink-500/10', text: 'text-pink-500', ring: 'ring-pink-500/30', dot: 'bg-pink-500' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', ring: 'ring-emerald-500/30', dot: 'bg-emerald-500' },
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-500', ring: 'ring-sky-500/30', dot: 'bg-sky-500' },
    indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', ring: 'ring-indigo-500/30', dot: 'bg-indigo-500' },
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-500', ring: 'ring-violet-500/30', dot: 'bg-violet-500' },
};

const Card = ({ title, icon: Icon, accent = 'sky', children, action, className = '', testId }) => {
    const t = TONES[accent] || TONES.sky;
    return (
        <div className={`bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${className}`} data-testid={testId}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2.5 min-w-0">
                    {Icon && (
                        <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center ring-1 ${t.ring}`}>
                            <Icon className={`w-4 h-4 ${t.text}`} />
                        </div>
                    )}
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-100 truncate">{title}</h3>
                </div>
                {action}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
};

const KpiCard = ({ kpi }) => {
    const t = TONES[kpi.tone] || TONES.sky;
    const Icon = kpi.icon;
    const TrendIcon = kpi.trend === 'up' ? ArrowUpRight : ArrowDownRight;
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 px-4 py-4 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="flex items-start justify-between mb-2">
                <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center ring-1 ${t.ring}`}>
                    <Icon className={`w-4.5 h-4.5 ${t.text}`} />
                </div>
                <div className={`inline-flex items-center gap-0.5 text-xs font-semibold ${kpi.trend === 'up' ? (kpi.tone === 'emerald' || kpi.tone === 'sky' ? 'text-emerald-500' : 'text-rose-500') : 'text-emerald-500'}`}>
                    <TrendIcon className="w-3.5 h-3.5" />
                    {kpi.delta}
                </div>
            </div>
            <div className="text-2xl font-bold text-slate-800 dark:text-white tabular-nums">{kpi.value}</div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">{kpi.label}</div>
            <div className="text-[11px] text-slate-400 mt-1">{kpi.sub}</div>
        </motion.div>
    );
};

const SeverityBadge = ({ tone, children }) => {
    const t = TONES[tone] || TONES.sky;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${t.bg} ${t.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
            {children}
        </span>
    );
};

const Bar = ({ value, max, tone = 'sky' }) => {
    const t = TONES[tone] || TONES.sky;
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
        <div className="w-full h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div className={`h-full ${t.dot} rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
    );
};

const fmtUsd = (n) => `$${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}K`;

// ============== MAIN COMPONENT ==============

const ForwarderDashboard = () => {
    const [range, setRange] = useState('today');

    const exportMax = useMemo(() => Math.max(...EXPORT_MILESTONES.map(s => s.count)), []);
    const importMax = useMemo(() => Math.max(...IMPORT_MILESTONES.map(s => s.count)), []);

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-slate-950" data-testid="forwarder-dashboard">
            {/* HERO HEADER */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#0A34A0] via-[#0E58E0] to-[#00C6FB] text-white">
                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
                <div className="relative px-6 py-7 max-w-[1600px] mx-auto">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-cyan-200 text-xs uppercase tracking-[0.2em] font-semibold mb-2">
                                <Activity className="w-3.5 h-3.5" />
                                Operations Control Tower
                            </div>
                            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Good morning, Captain.</h1>
                            <p className="text-cyan-100/80 text-sm mt-1">Here is what needs your attention today — {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {['today', '7d', '30d', 'mtd'].map((r) => (
                                <button
                                    key={r}
                                    onClick={() => setRange(r)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${range === r ? 'bg-white text-slate-800 shadow' : 'bg-white/10 text-white hover:bg-white/20'}`}
                                    data-testid={`range-${r}`}
                                >
                                    {r === 'today' ? 'Today' : r === '7d' ? 'Last 7d' : r === '30d' ? 'Last 30d' : 'Month-to-date'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
                {/* KPI STRIP */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="kpi-strip">
                    {CRITICAL_KPIS.map((kpi) => <KpiCard key={kpi.label} kpi={kpi} />)}
                </div>

                {/* PRIORITY EVENTS - hero section */}
                <Card
                    title="Highest Priority Daily Events"
                    icon={Flame}
                    accent="red"
                    testId="priority-events-card"
                    action={
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-500">
                            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                            {PRIORITY_EVENTS.length} need ownership
                        </span>
                    }
                >
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 -mx-5">
                        {PRIORITY_EVENTS.map((e) => (
                            <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group">
                                <div className="flex items-center gap-3 min-w-0">
                                    <SeverityBadge tone={e.tone}>{e.impact}</SeverityBadge>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-slate-700 dark:text-slate-100 truncate">{e.title}</div>
                                        <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                                            <span>{e.shipment}</span>
                                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                                            <span className="font-medium">{e.tag}</span>
                                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {e.age}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className={`text-xs font-medium ${e.owner === 'Unassigned' ? 'text-rose-500' : 'text-slate-500 dark:text-slate-400'}`}>{e.owner}</span>
                                    <button className="text-xs px-2.5 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition" data-testid={`event-act-${e.id}`}>Act</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* TWO COLUMN: SHIPMENT MOVEMENT */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card title="Export Pipeline · Milestones" icon={Ship} accent="indigo" testId="export-pipeline" action={<span className="text-xs font-medium text-slate-500">Container journey</span>}>
                        <div className="space-y-3">
                            {EXPORT_MILESTONES.map((m, i) => (
                                <div key={m.stage} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                            <span className="w-5 h-5 rounded-md bg-indigo-500/10 text-indigo-500 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                                            <span className="font-medium">{m.stage}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-700 dark:text-slate-100 tabular-nums">{m.count}</span>
                                            <span className={`text-[10px] font-semibold ${m.change.startsWith('+') ? 'text-emerald-500' : 'text-rose-500'}`}>{m.change}</span>
                                        </div>
                                    </div>
                                    <Bar value={m.count} max={exportMax} tone="indigo" />
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card title="Import Pipeline · Milestones" icon={Anchor} accent="violet" testId="import-pipeline" action={<span className="text-xs font-medium text-slate-500">From pre-alert to empty return</span>}>
                        <div className="space-y-3">
                            {IMPORT_MILESTONES.map((m, i) => (
                                <div key={m.stage} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                            <span className="w-5 h-5 rounded-md bg-violet-500/10 text-violet-500 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                                            <span className="font-medium">{m.stage}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-700 dark:text-slate-100 tabular-nums">{m.count}</span>
                                            <span className={`text-[10px] font-semibold ${m.change.startsWith('+') ? 'text-emerald-500' : 'text-rose-500'}`}>{m.change}</span>
                                        </div>
                                    </div>
                                    <Bar value={m.count} max={importMax} tone="violet" />
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* EXCEPTIONS + FINANCIAL */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card title="Exception & Alert Monitor" icon={Zap} accent="amber" className="lg:col-span-2" testId="exceptions-card">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {EXCEPTIONS_FEED.map((ex) => {
                                const t = TONES[ex.tone];
                                return (
                                    <div key={ex.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700 transition">
                                        <div className={`w-8 h-8 rounded-lg ${t.bg} flex items-center justify-center flex-shrink-0`}>
                                            <AlertCircle className={`w-4 h-4 ${t.text}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-slate-700 dark:text-slate-100 truncate">{ex.type}</div>
                                            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mt-0.5">{ex.severity}</div>
                                        </div>
                                        <div className="text-2xl font-bold text-slate-700 dark:text-slate-100 tabular-nums">{ex.count}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <Card title="Financial Pulse" icon={DollarSign} accent="emerald" testId="financial-card">
                        <div className="space-y-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Revenue · Today</div>
                                <div className="text-2xl font-bold text-slate-800 dark:text-white">${(FINANCIAL_PULSE.revenueToday / 1000).toFixed(1)}K</div>
                                <div className="text-xs text-slate-500">MTD ${(FINANCIAL_PULSE.revenueMTD / 1000000).toFixed(2)}M · GP {FINANCIAL_PULSE.gpMargin}%</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                    <div className="text-slate-500 mb-0.5">Receivables</div>
                                    <div className="font-bold text-slate-700 dark:text-slate-100">{fmtUsd(FINANCIAL_PULSE.receivables.current + FINANCIAL_PULSE.receivables.d30 + FINANCIAL_PULSE.receivables.d60 + FINANCIAL_PULSE.receivables.d90)}</div>
                                </div>
                                <div className="p-2.5 rounded-lg bg-rose-500/5 ring-1 ring-rose-500/10">
                                    <div className="text-rose-500 mb-0.5 font-medium">Overdue</div>
                                    <div className="font-bold text-rose-500">{fmtUsd(FINANCIAL_PULSE.overdue)}</div>
                                </div>
                                <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                    <div className="text-slate-500 mb-0.5">Unbilled</div>
                                    <div className="font-bold text-slate-700 dark:text-slate-100">{fmtUsd(FINANCIAL_PULSE.unbilled)}</div>
                                </div>
                                <div className="p-2.5 rounded-lg bg-amber-500/5 ring-1 ring-amber-500/10">
                                    <div className="text-amber-600 mb-0.5 font-medium">Payables Due</div>
                                    <div className="font-bold text-amber-600">{fmtUsd(FINANCIAL_PULSE.payables)}</div>
                                </div>
                            </div>
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                                <div className="flex justify-between text-xs">
                                    <span className="text-slate-500">AR Aging</span>
                                    <span className="text-slate-400 text-[10px]">Current · 30 · 60 · 90+</span>
                                </div>
                                <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                                    <div className="bg-emerald-500" style={{ width: `${(FINANCIAL_PULSE.receivables.current / 738100) * 100}%` }} />
                                    <div className="bg-amber-500" style={{ width: `${(FINANCIAL_PULSE.receivables.d30 / 738100) * 100}%` }} />
                                    <div className="bg-orange-500" style={{ width: `${(FINANCIAL_PULSE.receivables.d60 / 738100) * 100}%` }} />
                                    <div className="bg-rose-500" style={{ width: `${(FINANCIAL_PULSE.receivables.d90 / 738100) * 100}%` }} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center pt-2">
                                <div>
                                    <div className="text-base font-bold text-slate-700 dark:text-slate-100">{FINANCIAL_PULSE.invoicesGenerated}</div>
                                    <div className="text-[10px] text-slate-500">Invoices</div>
                                </div>
                                <div>
                                    <div className="text-base font-bold text-rose-500">{FINANCIAL_PULSE.negativeMargin}</div>
                                    <div className="text-[10px] text-slate-500">Neg margin</div>
                                </div>
                                <div>
                                    <div className="text-base font-bold text-amber-600">{fmtUsd(FINANCIAL_PULSE.revenueLeakage)}</div>
                                    <div className="text-[10px] text-slate-500">Leakage</div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* CUSTOMER + DOC + COMPLIANCE */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card title="Customer Pulse · VIP & Active" icon={Users} accent="sky" testId="customer-card">
                        <div className="space-y-2">
                            {CUSTOMER_PULSE.map((c) => {
                                const t = TONES[c.tone];
                                return (
                                    <div key={c.name} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-100 truncate">{c.name}</span>
                                                {c.tier === 'VIP' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase">VIP</span>}
                                                {c.tier === 'Gold' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-bold uppercase">Gold</span>}
                                                {c.tier === 'Silver' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold uppercase">Silver</span>}
                                            </div>
                                            <div className="text-[11px] text-slate-400">{c.shipments} active · last update {c.ageHrs}h ago</div>
                                        </div>
                                        <div className={`text-xs font-semibold ${t.text}`}>{c.status}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <div className="text-center">
                                <div className="text-lg font-bold text-slate-700 dark:text-slate-100">{COMMS_KPI.openTickets}</div>
                                <div className="text-[10px] text-slate-500">Open tickets</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-emerald-500">{COMMS_KPI.avgResponseMin}m</div>
                                <div className="text-[10px] text-slate-500">Avg response</div>
                            </div>
                            <div className="text-center">
                                <div className="text-lg font-bold text-amber-600">{COMMS_KPI.pendingQueries}</div>
                                <div className="text-[10px] text-slate-500">Pending</div>
                            </div>
                        </div>
                    </Card>

                    <Card title="Documentation Monitor" icon={FileText} accent="orange" testId="doc-card">
                        <div className="space-y-2.5 text-sm">
                            <div className="flex items-center justify-between p-2 rounded-lg bg-rose-500/5">
                                <span className="text-rose-500 font-medium">Missing documents</span>
                                <span className="font-bold text-rose-500">{DOC_MONITOR.missingDocs}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">Pending HBL issuance</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-100">{DOC_MONITOR.pendingHBL}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">Pending MBL verify</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-100">{DOC_MONITOR.pendingMBL}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">Commercial invoices</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-100">{DOC_MONITOR.pendingCommercialInvoice}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">COO/Certificates</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-100">{DOC_MONITOR.pendingCOO}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">Weight discrepancies</span>
                                <span className="font-semibold text-amber-600">{DOC_MONITOR.weightDisc}</span>
                            </div>
                            <div className="flex items-center justify-between px-2 py-1.5">
                                <span className="text-slate-600 dark:text-slate-300">HS code issues</span>
                                <span className="font-semibold text-amber-600">{DOC_MONITOR.hsCodeIssues}</span>
                            </div>
                            <div className="flex items-center justify-between p-2 rounded-lg bg-amber-500/5 mt-1">
                                <span className="text-amber-600 font-medium inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Filing deadline ≤ 24h</span>
                                <span className="font-bold text-amber-600">{DOC_MONITOR.filingDeadlines24h}</span>
                            </div>
                        </div>
                    </Card>

                    <Card title="Customs & Regulatory" icon={ShieldAlert} accent="rose" testId="compliance-card">
                        <div className="space-y-2.5">
                            {COMPLIANCE.map((c) => {
                                const t = TONES[c.tone];
                                return (
                                    <div key={c.code} className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                                        <div className="col-span-3">
                                            <div className="text-sm font-bold text-slate-700 dark:text-slate-100">{c.code}</div>
                                            <div className="text-[10px] text-slate-400">filing</div>
                                        </div>
                                        <div className="col-span-3 text-right">
                                            <div className="text-sm font-semibold text-slate-700 dark:text-slate-100 tabular-nums">{c.filings}</div>
                                            <div className="text-[10px] text-slate-400">filed</div>
                                        </div>
                                        <div className="col-span-3 text-right">
                                            <div className={`text-sm font-bold ${c.failed > 0 ? 'text-rose-500' : 'text-emerald-500'} tabular-nums`}>{c.failed}</div>
                                            <div className="text-[10px] text-slate-400">failed</div>
                                        </div>
                                        <div className="col-span-3 text-right">
                                            <div className={`text-xs font-bold ${t.text} tabular-nums`}>{c.deadline}</div>
                                            <div className="text-[10px] text-slate-400">deadline</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-slate-500">Sanction screening</span>
                                <span className="ml-auto font-bold text-emerald-500">Clean</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-slate-500">DG compliance</span>
                                <span className="ml-auto font-bold text-emerald-500">Pass</span>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* CONTAINER EQUIPMENT + CARRIER + SALES */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card title="Container & Equipment" icon={Container} accent="indigo" testId="container-card">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold"><Box className="w-3.5 h-3.5" /> Empty avail</div>
                                <div className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{CONTAINER_EQUIP.emptyAvailable}</div>
                            </div>
                            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold"><Package className="w-3.5 h-3.5" /> In yard</div>
                                <div className="text-2xl font-bold text-slate-800 dark:text-white mt-1">{CONTAINER_EQUIP.inYard}</div>
                            </div>
                            <div className="p-3 rounded-xl bg-emerald-500/5 ring-1 ring-emerald-500/10">
                                <div className="text-[11px] uppercase tracking-wider text-emerald-500 font-semibold">Utilization</div>
                                <div className="text-2xl font-bold text-emerald-500 mt-1">{CONTAINER_EQUIP.utilization}%</div>
                            </div>
                            <div className="p-3 rounded-xl bg-rose-500/5 ring-1 ring-rose-500/10">
                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-rose-500 font-semibold"><Flame className="w-3.5 h-3.5" /> Demurrage</div>
                                <div className="text-2xl font-bold text-rose-500 mt-1">${(CONTAINER_EQUIP.demurrageExposure / 1000).toFixed(1)}K</div>
                            </div>
                        </div>
                        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-3 gap-2 text-center text-xs">
                            <div>
                                <div className="text-slate-700 dark:text-slate-100 font-bold">{CONTAINER_EQUIP.avgDetentionDays}d</div>
                                <div className="text-slate-400 text-[10px]">Avg detention</div>
                            </div>
                            <div>
                                <div className="text-amber-600 font-bold">{CONTAINER_EQUIP.chassisShortage}</div>
                                <div className="text-slate-400 text-[10px]">Chassis short</div>
                            </div>
                            <div>
                                <div className="text-rose-500 font-bold">{CONTAINER_EQUIP.damaged}</div>
                                <div className="text-slate-400 text-[10px]">Damaged</div>
                            </div>
                        </div>
                        <div className="mt-3 flex justify-between text-[11px] text-slate-500">
                            <span>SOC <strong className="text-slate-700 dark:text-slate-200">{CONTAINER_EQUIP.soc}</strong></span>
                            <span>COC <strong className="text-slate-700 dark:text-slate-200">{CONTAINER_EQUIP.coc}</strong></span>
                        </div>
                    </Card>

                    <Card title="Carrier & Schedule Pulse" icon={Ship} accent="sky" testId="carrier-card">
                        <div className="space-y-1.5">
                            <div className="grid grid-cols-12 gap-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold pb-1.5 border-b border-slate-100 dark:border-slate-800">
                                <div className="col-span-4">Carrier</div>
                                <div className="col-span-3 text-right">Reliab.</div>
                                <div className="col-span-2 text-right">Blank</div>
                                <div className="col-span-3 text-right">Rate Δ</div>
                            </div>
                            {CARRIER_PULSE.map((c) => {
                                const t = TONES[c.tone];
                                return (
                                    <div key={c.carrier} className="grid grid-cols-12 gap-1 items-center text-xs px-1 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <div className="col-span-4 font-medium text-slate-700 dark:text-slate-100">{c.carrier}</div>
                                        <div className="col-span-3 text-right">
                                            <span className={`font-bold ${c.reliability >= 85 ? 'text-emerald-500' : c.reliability >= 75 ? 'text-amber-500' : 'text-rose-500'}`}>{c.reliability}%</span>
                                        </div>
                                        <div className="col-span-2 text-right">
                                            <span className={c.blank > 0 ? 'text-rose-500 font-bold' : 'text-slate-400'}>{c.blank}</span>
                                        </div>
                                        <div className="col-span-3 text-right">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.bg} ${t.text}`}>{c.rateChg}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <Card title="Sales & Pipeline" icon={Briefcase} accent="violet" testId="sales-card">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="text-center p-2 rounded-lg bg-violet-500/5">
                                <div className="text-xl font-bold text-violet-500">{SALES_PULSE.newQuotes}</div>
                                <div className="text-[10px] text-slate-500">New quotes</div>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-violet-500/5">
                                <div className="text-xl font-bold text-violet-500">{SALES_PULSE.rfqs}</div>
                                <div className="text-[10px] text-slate-500">RFQs</div>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-violet-500/5">
                                <div className="text-xl font-bold text-violet-500">{SALES_PULSE.spotRequests}</div>
                                <div className="text-[10px] text-slate-500">Spot req</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
                            <span className="text-slate-500">Win rate · 30d</span>
                            <span className="font-bold text-emerald-500">{SALES_PULSE.winRate}%</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Pipeline</span>
                            <span className="font-bold text-slate-700 dark:text-slate-100">${(SALES_PULSE.pipeline / 1000).toFixed(0)}K</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Top trade lanes</div>
                            <div className="space-y-1.5">
                                {SALES_PULSE.topLanes.map((l) => (
                                    <div key={l.lane} className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <MapPin className="w-3 h-3 text-violet-500 flex-shrink-0" />
                                            <span className="font-medium text-slate-700 dark:text-slate-100 truncate">{l.lane}</span>
                                        </div>
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <span className="text-slate-500 tabular-nums">{l.quotes}q</span>
                                            <span className="font-bold text-slate-700 dark:text-slate-100 tabular-nums">${(l.revenue / 1000).toFixed(0)}K</span>
                                            <span className={`text-[10px] font-bold tabular-nums ${l.gp >= 20 ? 'text-emerald-500' : 'text-amber-500'}`}>{l.gp}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* FOOTER STATUS BAR */}
                <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 rounded-2xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                    <div className="flex items-center gap-2 text-white">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="font-semibold">Control Tower · Live</span>
                    </div>
                    <div className="text-slate-400">All AI agents online</div>
                    <div className="text-slate-400">Carrier feeds: 6/6</div>
                    <div className="text-slate-400">Last sync · 2m ago</div>
                    <div className="ml-auto flex items-center gap-2 text-slate-400">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} local</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForwarderDashboard;
